/**
 * @file Predictive search combobox (snippets/search-dialog.liquid, sections/predictive-search.liquid).
 *
 * <bk-predictive-search data-url data-section data-types data-limit data-dialog data-error>
 * - Debounced (250 ms) GET of the predictive search route with section_id (Section Rendering API),
 *   one AbortController per request, results cached per query for the visit.
 * - Injects the section's [data-predictive-search] root into [data-search-results]; the empty state
 *   ([data-search-empty]: popular categories, brands) shows while the field is empty and after an
 *   empty result.
 * - Combobox (ARIA 1.2): aria-expanded / aria-controls / aria-activedescendant on the input;
 *   Up and Down move through the role="option" links (wrapping); Enter follows the active option,
 *   otherwise the form submits to the search page; Escape clears a filled field, and the next
 *   Escape (or Escape from any control in the panel) closes the dialog.
 * - The result count, the no-results sentence and errors go to the polite [data-search-status].
 * - Any [data-search-open] control opens the search dialog ([data-search-dialog]); a non-empty
 *   value prefills and runs the query.
 */
import { debounce, withTimeout } from '@theme/utils';

const DEBOUNCE_MS = 250;
const CACHE_LIMIT = 30;

class BkPredictiveSearch extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {AbortController | null} */
  #request = null;

  /** @type {Map<string, string>} */
  #cache = new Map();

  #activeIndex = -1;

  #renderedTerms = '';

  #debouncedLoad = debounce((/** @type {string} */ terms) => {
    this.#load(terms);
  }, DEBOUNCE_MS);

  connectedCallback() {
    const input = this.#input();
    if (!input) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    input.addEventListener('input', () => this.#onInput(), { signal });
    input.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    this.addEventListener('keydown', (event) => this.#onEscape(event), { signal });
    this.querySelector('[data-search-clear]')?.addEventListener('click', (event) => this.#onClear(event), { signal });
    document.addEventListener('bk:dialog:open', (event) => this.#onDialogOpen(event), { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#request?.abort();
    this.#request = null;
    this.#debouncedLoad.cancel();
  }

  /**
   * Put `terms` in the field and search right away.
   * @param {string} terms
   */
  search(terms) {
    const input = this.#input();
    if (!input) return;
    input.value = terms;
    this.#onInput({ immediate: true });
  }

  /** @returns {HTMLInputElement | null} */
  #input() {
    const input = this.querySelector('[data-search-input]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @returns {HTMLElement | null} */
  #results() {
    const results = this.querySelector('[data-search-results]');
    return results instanceof HTMLElement ? results : null;
  }

  /** @returns {HTMLElement[]} */
  #options() {
    const results = this.#results();
    if (!results) return [];
    return [...results.querySelectorAll('[role="option"]')].filter((option) => option instanceof HTMLElement);
  }

  /** @returns {string} */
  #currentTerms() {
    return this.#input()?.value.trim() ?? '';
  }

  /** @param {{ immediate?: boolean }} [options] */
  #onInput({ immediate = false } = {}) {
    const input = this.#input();
    if (!input) return;

    const clear = this.querySelector('[data-search-clear]');
    if (clear instanceof HTMLElement) clear.hidden = input.value.length === 0;

    const terms = input.value.trim();
    if (!terms) {
      this.#debouncedLoad.cancel();
      this.#request?.abort();
      this.#showEmptyState();
      return;
    }

    if (immediate) {
      this.#debouncedLoad.cancel();
      this.#load(terms);
    } else {
      this.#debouncedLoad(terms);
    }
  }

  /** @param {string} terms */
  async #load(terms) {
    if (terms === this.#renderedTerms) return;

    const cached = this.#cache.get(terms);
    if (cached) {
      this.#render(cached, terms);
      return;
    }

    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(this.dataset.url || '/search/suggest', window.location.origin);
      url.searchParams.set('q', terms);
      url.searchParams.set('resources[type]', this.dataset.types || 'product,collection,query');
      url.searchParams.set('resources[limit]', this.dataset.limit || '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('section_id', this.dataset.section || 'predictive-search');

      const response = await fetch(url, { signal: withTimeout(request.signal), headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Predictive search: HTTP ${response.status}`);

      const html = await response.text();
      const root = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-search]');
      if (!root) throw new Error('Predictive search: section markup missing');

      const markup = root.outerHTML;
      this.#remember(terms, markup);
      if (this.#currentTerms() === terms) this.#render(markup, terms);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (this.#currentTerms() === terms) this.#renderError();
    } finally {
      if (this.#request === request) {
        this.#request = null;
        this.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * @param {string} terms
   * @param {string} markup
   */
  #remember(terms, markup) {
    this.#cache.set(terms, markup);
    if (this.#cache.size <= CACHE_LIMIT) return;
    const oldest = this.#cache.keys().next().value;
    if (oldest !== undefined) this.#cache.delete(oldest);
  }

  /**
   * @param {string} markup - Server-rendered [data-predictive-search] root (same origin)
   * @param {string} terms
   */
  #render(markup, terms) {
    const results = this.#results();
    const input = this.#input();
    if (!results || !input) return;

    results.innerHTML = markup;
    results.hidden = false;
    this.#renderedTerms = terms;
    this.#activeIndex = -1;
    input.removeAttribute('aria-activedescendant');

    const root = results.querySelector('[data-predictive-search]');
    const count = Number(root?.getAttribute('data-results-count') ?? 0);
    const listbox = results.querySelector('[role="listbox"]');
    if (listbox?.id) {
      input.setAttribute('aria-controls', listbox.id);
      input.setAttribute('aria-expanded', 'true');
    } else {
      input.removeAttribute('aria-controls');
      input.setAttribute('aria-expanded', 'false');
    }

    const empty = this.querySelector('[data-search-empty]');
    if (empty instanceof HTMLElement) empty.hidden = count > 0;

    this.#announce(root?.querySelector('[data-predictive-status]')?.textContent?.trim() ?? '');
  }

  #renderError() {
    const results = this.#results();
    if (!results) return;
    const message = this.dataset.error ?? '';
    const paragraph = document.createElement('p');
    paragraph.className = 'predictive-search__error';
    paragraph.textContent = message;
    results.replaceChildren(paragraph);
    results.hidden = false;
    this.#renderedTerms = '';
    this.#collapse();
    this.#announce(message);
  }

  #showEmptyState() {
    const results = this.#results();
    if (results) {
      results.hidden = true;
      results.replaceChildren();
    }
    const empty = this.querySelector('[data-search-empty]');
    if (empty instanceof HTMLElement) empty.hidden = false;
    this.#renderedTerms = '';
    this.#collapse();
    this.#announce('');
  }

  #collapse() {
    const input = this.#input();
    this.#activeIndex = -1;
    if (!input) return;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-controls');
    input.removeAttribute('aria-activedescendant');
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.querySelector('[data-search-status]');
    if (!status) return;
    status.textContent = '';
    if (!message) return;
    // A fresh text node after a tick is announced reliably by screen readers.
    setTimeout(() => {
      status.textContent = message;
    }, 60);
  }

  /** @param {number} index */
  #setActive(index) {
    const input = this.#input();
    if (!input) return;
    const options = this.#options();
    for (const [position, option] of options.entries()) {
      option.setAttribute('aria-selected', String(position === index));
    }
    const active = options[index];
    this.#activeIndex = active ? index : -1;
    if (!active) {
      input.removeAttribute('aria-activedescendant');
      return;
    }
    input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
  }

  /** @param {KeyboardEvent} event */
  #onKeydown(event) {
    if (event.isComposing) return;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        const options = this.#options();
        if (options.length === 0) return;
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        let start = this.#activeIndex;
        if (start === -1) start = step === 1 ? -1 : options.length;
        this.#setActive((start + step + options.length) % options.length);
        break;
      }
      case 'Enter': {
        const option = this.#options()[this.#activeIndex];
        if (!option) return;
        event.preventDefault();
        option.click();
        break;
      }
      default:
    }
  }

  /**
   * Escape anywhere in the search panel: a filled field is cleared first, then the dialog closes.
   * The dialog is closed here rather than through the native cancel request, because a
   * type="search" field swallows Escape in some browsers and the dialog would never hear it.
   * @param {KeyboardEvent} event
   */
  #onEscape(event) {
    if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;
    const input = this.#input();

    if (input && event.target === input && input.value !== '') {
      event.preventDefault();
      input.value = '';
      this.#onInput();
      return;
    }

    const dialog = /** @type {any} */ (this.closest('bk-dialog'));
    if (!dialog?.isOpen || typeof dialog.close !== 'function') return;
    event.preventDefault();
    dialog.close();
  }

  /** @param {MouseEvent} event */
  #onClear(event) {
    event.preventDefault();
    const input = this.#input();
    if (!input) return;
    input.value = '';
    this.#onInput();
    input.focus();
  }

  /** @param {Event} event */
  #onDialogOpen(event) {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail?.id || detail.id !== this.dataset.dialog) return;
    const input = this.#input();
    if (!input || !input.value.trim()) return;
    input.select();
    if (!this.#renderedTerms) this.#onInput({ immediate: true });
  }
}

if (!customElements.get('bk-predictive-search')) customElements.define('bk-predictive-search', BkPredictiveSearch);

/** Any [data-search-open] control opens the search dialog; a non-empty value prefills the query. */
document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const target = event.target;
  const opener = target instanceof Element ? target.closest('[data-search-open]') : null;
  if (!(opener instanceof HTMLElement)) return;

  const dialog = /** @type {any} */ (document.querySelector('[data-search-dialog]'));
  if (!dialog || typeof dialog.open !== 'function') return;
  event.preventDefault();
  dialog.open(opener);

  const terms = (opener.dataset.searchOpen ?? '').trim();
  const search = /** @type {any} */ (dialog.querySelector('bk-predictive-search'));
  if (terms && search && typeof search.search === 'function') search.search(terms);
});
